import { useTranslation } from 'react-i18next';
import { Progress } from '@monksflow/monks-ui';
import { EM_DASH, formatMetric } from '@/shared/utils/metric-format';

export interface SpendProgressProps {
  /** Spend so far in this period, or null when no platform reported any. */
  spendToDate: number | null;
  /** Where the run rate says spend will land by the end of the flight. */
  projectedSpend: number | null;
  currency: string | null;
  /** Named in the bar's accessible label, so a screen reader knows whose bar this is. */
  campaignName: string;
}

/**
 * Delivered spend against its projection, as one bar rather than two number columns.
 *
 * The bar is drawn only when both figures are known and the projection is positive. A
 * missing projection leaves the delivered figure standing alone with its reason: a full
 * bar would claim the campaign had spent everything it was going to, and an empty one
 * would claim the opposite, and neither is a measurement anybody made.
 */
export function SpendProgress({
  spendToDate,
  projectedSpend,
  currency,
  campaignName,
}: SpendProgressProps) {
  const { t } = useTranslation('projects');
  const canPlot = spendToDate !== null && projectedSpend !== null && projectedSpend > 0;
  // Clamped for the bar's width only; the figures beside it are untouched. Radix draws
  // the fill by shifting a full-width indicator, so a value past 100 would push it out of
  // the track rather than reading as full.
  const pct = canPlot ? Math.min((spendToDate / projectedSpend) * 100, 100) : null;

  return (
    <div className="flex min-w-44 flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span>{formatMetric(spendToDate, 'currency', currency)}</span>
        {/* The projection's own slot, so a missing one is an em dash where the figure
            would have been rather than a gap the eye reads as "nothing to say here". */}
        <span className="text-muted-foreground">
          {projectedSpend === null
            ? EM_DASH
            : t('performancePacing.spendProgress.ofAmount', {
                amount: formatMetric(projectedSpend, 'currency', currency),
              })}
        </span>
      </div>

      {pct === null ? (
        <span className="text-xs text-muted-foreground">
          {projectedSpend === null
            ? t('performancePacing.spendProgress.noProjectionYet')
            : t('performancePacing.spendProgress.noDeliveredSpendReported')}
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <Progress
            value={pct}
            className="h-1.5 flex-1"
            aria-label={t('performancePacing.spendProgress.deliveredAgainstProjected', {
              name: campaignName,
            })}
          />
          <span className="text-xs tabular-nums text-muted-foreground">{pct.toFixed(0)}%</span>
        </div>
      )}
    </div>
  );
}
